import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import ImportModal from '../common/ImportModal';
import { formatDate } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

const emptyFY = { name: '', fromDate: '', toDate: '' };

export default function FinancialYearMaster() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyFY);
  const [editing, setEditing] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const existingNames = useMemo(
    () => (state.financialYears || []).map(f => ({ id: f.id, value: f.name })),
    [state.financialYears]
  );

  const errors = useMemo(() => {
    const { errors } = validateForm({
      name: [
        validators.required(form.name, 'Financial year'),
        validators.unique(form.name, existingNames, 'Financial year', editing),
      ],
      fromDate: [validators.required(form.fromDate, 'From date')],
      toDate: [
        validators.required(form.toDate, 'To date'),
        form.fromDate && form.toDate && form.toDate <= form.fromDate ? 'To date must be after From date' : '',
      ],
    });
    return errors;
  }, [form, existingNames, editing]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (f) => setTouched(p => ({ ...p, [f]: true }));

  const handleSave = () => {
    setSubmitted(true);
    setTouched({ name: true, fromDate: true, toDate: true });
    if (hasErrors) return;
    if (editing) {
      dispatch({ type: 'UPDATE_FINANCIAL_YEAR', payload: { ...form, id: editing } });
      addToast('Financial year updated', 'success');
    } else {
      dispatch({ type: 'ADD_FINANCIAL_YEAR', payload: form });
      addToast('Financial year added', 'success');
    }
    close();
  };

  const close = () => { setShowForm(false); setForm(emptyFY); setEditing(null); setTouched({}); setSubmitted(false); };
  const openNew = () => { setForm(emptyFY); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); };
  const openEdit = (row) => { setForm({ name: row.name, fromDate: row.fromDate, toDate: row.toDate }); setEditing(row.id); setTouched({}); setSubmitted(false); setShowForm(true); };

  // Transaction collections that are tagged with a financial year.
  const TXN_KEYS = [
    'salesInvoices', 'salesReturns', 'deliveryChallans', 'salesOrders',
    'purchaseBills', 'purchaseReturns', 'purchaseOrders',
    'receipts', 'payments', 'journalEntries', 'expenses', 'contraEntries',
    'stockJournals', 'productions', 'stockIssues',
  ];
  const usageCount = (fy) => TXN_KEYS.reduce((n, k) =>
    n + (state[k] || []).filter(d =>
      d.financialYear === fy.name || (d.date && fy.fromDate && fy.toDate && d.date >= fy.fromDate && d.date <= fy.toDate)
    ).length, 0);

  const handleDelete = async (row) => {
    const used = usageCount(row);
    if (used > 0) {
      alert(`Cannot delete: ${used} transaction(s) are recorded in financial year ${row.name}. Once entries exist for a financial year, it cannot be deleted.`);
      return;
    }
    const ok = await confirm(`Delete financial year ${row.name}?`, { title: 'Delete Financial Year', variant: 'danger', confirmLabel: 'Delete' });
    if (ok) { dispatch({ type: 'DELETE_FINANCIAL_YEAR', payload: row.id }); addToast('Financial year deleted', 'success'); }
  };

  const columns = [
    { key: 'name', label: 'Financial Year', sortable: true },
    { key: 'fromDate', label: 'From Date', render: v => formatDate(v), exportValue: r => formatDate(r.fromDate) },
    { key: 'toDate', label: 'To Date', render: v => formatDate(v), exportValue: r => formatDate(r.toDate) },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Financial Year</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn btn-secondary flex items-center gap-2"><Upload size={16} /> Import</button>
          <button onClick={openNew} className="btn btn-primary flex items-center gap-2"><Plus size={16} /> Add Financial Year</button>
        </div>
      </div>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="Import Financial Years"
        dedupeKey="name"
        existing={(state.financialYears || []).map(f => f.name)}
        columns={[
          { key: 'name', label: 'Financial Year', required: true },
          { key: 'fromDate', label: 'From Date (YYYY-MM-DD)', required: true },
          { key: 'toDate', label: 'To Date (YYYY-MM-DD)', required: true },
        ]}
        sample={{ name: '2025-26', fromDate: '2025-04-01', toDate: '2026-03-31' }}
        onAdd={(p) => dispatch({ type: 'ADD_FINANCIAL_YEAR', payload: { ...emptyFY, ...p } })}
      />

      <div className="card">
        <DataTable
          columns={columns}
          data={state.financialYears || []}
          searchFields={['name']}
          exportFilename="financial-years"
          emptyState={{ title: 'No financial years yet', description: 'Define your financial years (e.g. 2025-26) so transactions are tracked year-wise.', actionLabel: 'Add Financial Year', onAction: openNew }}
          actions={(row) => (
            <>
              <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={close} title={editing ? 'Edit Financial Year' : 'Add Financial Year'}>
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        <div className="space-y-4">
          <FormField label="Financial Year" required error={errors.name} touched={touched.name || submitted} hint="e.g. 2025-26">
            <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} onBlur={() => touch('name')} maxLength={20} placeholder="2025-26" autoFocus />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="From Date" required error={errors.fromDate} touched={touched.fromDate || submitted}>
              <input className="input" type="date" value={form.fromDate} onChange={e => setForm({ ...form, fromDate: e.target.value })} onBlur={() => touch('fromDate')} />
            </FormField>
            <FormField label="To Date" required error={errors.toDate} touched={touched.toDate || submitted}>
              <input className="input" type="date" value={form.toDate} onChange={e => setForm({ ...form, toDate: e.target.value })} onBlur={() => touch('toDate')} min={form.fromDate} />
            </FormField>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={close} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={submitted && hasErrors}>{editing ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
